import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PrivateRoute from './PrivateRoute';
import Root from './Root';
import * as layoutConstants from '../constants/layout';

// All layouts/containers
import DefaultLayout from '../layouts/Default';
import VerticalLayout from '../layouts/Vertical';
import DetachedLayout from '../layouts/Detached';
import HorizontalLayout from '../layouts/Horizontal';
import FullLayout from '../layouts/Full';
import LabelAndDocs from '../pages/LabelAndDocs';

// lazy load all the views

const Addresses = React.lazy(() => import('../pages/addresses'));

const Warehouses = React.lazy(() => import('../pages/warehouses'));
const Locations = React.lazy(() => import('../pages/locations'));
const Inventory = React.lazy(() => import('../pages/inventory'));

// auth
const Login = React.lazy(() => import('../pages/account/Login'));
const Logout = React.lazy(() => import('../pages/account/Logout'));
const Register = React.lazy(() => import('../pages/account/Register'));
const ForgetPassword = React.lazy(() => import('../pages/account/ForgetPassword'));
const ResetPassword = React.lazy(() => import('../pages/reset-password/index'));
const LockScreen = React.lazy(() => import('../pages/account/LockScreen'));

const Login2 = React.lazy(() => import('../pages/account2/Login2'));
const Logout2 = React.lazy(() => import('../pages/account2/Logout2'));
const Register2 = React.lazy(() => import('../pages/account2/Register2'));
const Confirm2 = React.lazy(() => import('../pages/account2/Confirm2'));
const ForgetPassword2 = React.lazy(() => import('../pages/account2/ForgetPassword2'));
const LockScreen2 = React.lazy(() => import('../pages/account2/LockScreen2'));

// pages
const Profile = React.lazy(() => import('../pages/profile'));
const Profile2 = React.lazy(() => import('../pages/profile2'));
const ProfileAccount = React.lazy(() => import('../pages/profile-account'));
const Billing = React.lazy(() => import('../pages/billing'));
const Plan = React.lazy(() => import('../pages/plan'));
const ViewPlans = React.lazy(() => import('../pages/plan/view-plans'));
const ViewInvoice = React.lazy(() => import('../pages/invoice'));
const Users = React.lazy(() => import('../pages/account2/users'));
const Batches = React.lazy(() => import('../pages/batches'));
const Returns = React.lazy(() => import('../pages/returns'));
const OrderReport = React.lazy(() => import('../components/Reports/OrderReport'));
// const ProductReport = React.lazy(() => import('../components/Reports/ProductReport'));
const ShipmentReport = React.lazy(() => import('../components/Reports/ShipmentReport'));
const ShipmentSurchargesReport = React.lazy(() => import('../components/Reports/ShipmentSurchargesReport'));
const NonShipmentSurchargesReport = React.lazy(() => import('../components/Reports/NonShipmentSurchargesReport'));
const AdjustmentsStandardReport = React.lazy(() => import('../components/Reports/AdjustmentsStandardReport'));
// const ChargesReport = React.lazy(() => import('../components/Reports/ChargesReport'));
const TrackingRecordsReport = React.lazy(() => import('../components/Reports/TrackingRecordsReport'));
const DelayedFacilitiesReport = React.lazy(() => import('../components/Reports/DelayedFacilitiesReport'));
const AggregatedDelayedShipmentsReport = React.lazy(() => import('../components/Reports/AggregatedDelayedShipmentsReport'));
const AddressReport = React.lazy(() => import('../components/Reports/AddressReport'));
// const CarrierReport = React.lazy(() => import('../components/Reports/CarrierReport'));
// const PaymentReport = React.lazy(() => import('../components/Reports/PaymentReport'));
const AuditReport = React.lazy(() => import('../components/Reports/AuditReport'));
const ActivityLogReport = React.lazy(() => import('../components/Reports/ActivityLogReport'));
const ErrorPageNotFound = React.lazy(() => import('../pages/error/PageNotFound'));
const ErrorPageNotFoundAlt = React.lazy(() => import('../pages/error/PageNotFoundAlt'));
const ServerError = React.lazy(() => import('../pages/error/ServerError'));
const CarrierAccounts = React.lazy(() => import('../pages/carrier'));

const Products = React.lazy(() => import('../pages/products'));
const NewProduct = React.lazy(() => import('../pages/products/new-product'));

// - other
const Maintenance = React.lazy(() => import('../pages/other/Maintenance'));

// shipments
const Shipments = React.lazy(() => import('../pages/shipments'));
const Agents = React.lazy(() => import('../pages/agents'));

// pickups
const PickupSchedule = React.lazy(() => import('../pages/pickup/PickupSchedule'));
// pickups
const ViewPickup = React.lazy(() => import('../pages/pickup/ViewPickup'));

// manifests
const Manifests = React.lazy(() => import('../pages/manifests'));
const CreateManifest = React.lazy(() => import('../pages/manifests/create-manifest'));

// Pickup
const Pickup = React.lazy(() => import('../pages/pickup'));

// manifest
const Manifest = React.lazy(() => import('../pages/manifest'));

// Pickups

// pickups-schedule.
// const pickupsSchedule = React.lazy(() => import('../pages/pickups-schedule'));

// orders
const Orders = React.lazy(() => import('../pages/orders'));
const EditOrder = React.lazy(() => import('../pages/orders/edit-order'));
const AddOrder = React.lazy(() => import('../pages/orders/new-order'));
const ViewOrder = React.lazy(() => import('../pages/orders/view-order'));

// location
const Location = React.lazy(() => import('../pages/location'));
const FindLocation = React.lazy(() => import('../pages/location/find-location'));
const ViewLocation = React.lazy(() => import('../pages/location/view-location'));

const StoreIntegrations = React.lazy(() => import('../pages/integrations/stores'));
const IntegrationApi = React.lazy(() => import('../pages/integrations/api'));
const TechnologyPartners = React.lazy(() => import('../pages/integrations/TechnologyPartners'));
const Webhooks = React.lazy(() => import('../pages/integrations/webhooks'));
const JobManagement = React.lazy(() => import('../pages/integrations/JobManagement'));
const Imports = React.lazy(() => import('../pages/imports'));
const ImportManage = React.lazy(() => import('../pages/imports/import-manage'));
//const ImportComparisonManage = React.lazy(() => import('../pages/imports/import-comparison-manage'));

// Settings
const Peripherals = React.lazy(() => import('../pages/settings/Peripherals/index'));
const Onboarding = React.lazy(() => import('../pages/onboarding'));
const ServiceLevels = React.lazy(() => import('../pages/settings/ServiceLevels'));
const Notifications = React.lazy(() => import('../pages/settings/Notifications'));
const Automation = React.lazy(() => import('../pages/automation'));
const AddAutomation = React.lazy(() => import('../pages/automation/AddAutomation'));
const Packages = React.lazy(() => import('../pages/packages'));
const RateSettings = React.lazy(() => import('../pages/rate-settings'));
const RateSets = React.lazy(() => import('../pages/rate-sets'));

// Enterprise Home
const EnterpriseDashboard = React.lazy(() => import('../pages/home/enterprise-dashboard'));

// Enterprise Analytics
const ShipmentsAnalyticsEnterprise = React.lazy(() => import('../pages/analytics-enterprise/shipments'));
const DeliveriesAnalyticsEnterprise = React.lazy(() => import('../pages/analytics-enterprise/deliveries'));
const FinancialsAnalyticsEnterprise = React.lazy(() => import('../pages/analytics-enterprise/financials'));
const CustomAnalyticsEnterprise = React.lazy(() => import('../pages/analytics-enterprise/custom'));
const ShipmentComparisonEnterprise = React.lazy(() => import('../pages/analytics-enterprise/shipment-comparison'));

// Enterprise Reports
const ShipmentsReports = React.lazy(() => import('../pages/reports-enterprise/ShipmentsReport'));
const RefundsReports = React.lazy(() => import('../pages/reports-enterprise/RefundsReport'));
const DeliveriesReportsEnterprise = React.lazy(() => import('../pages/reports-enterprise/deliveries'));
const ChargesReport = React.lazy(() => import('../pages/reports-enterprise/ChargesReport'));
const AdjustmentsReport = React.lazy(() => import('../pages/reports-enterprise/AdjustmentsReport'));
const MarginsReport = React.lazy(() => import('../pages/reports-enterprise/MarginsReport'));
const CarrierAccountsReportEnterprise = React.lazy(() => import('../pages/reports-enterprise/carrier-accounts'));
const CustomAccountingReport = React.lazy(() => import('../pages/reports-enterprise/CustomAccountingReport'));
const CustomCustomerReport = React.lazy(() => import('../pages/reports-enterprise/CustomCustomerReport'));
const CustomProfitabilityReport = React.lazy(() => import('../pages/reports-enterprise/CustomProfitabilityReport'));
const CustomAdjustmentsReport = React.lazy(() => import('../pages/reports-enterprise/CustomAdjustmentsReport'));
const CustomVerifiedMarginsReport = React.lazy(() => import('../pages/reports-enterprise/CustomVerifiedMarginsReport'));

// Enterprise Account Management
const AccountManagementEnterprise = React.lazy(() => import('../pages/account-management-enterprise'));
const UserManagementEnterprise = React.lazy(() => import('../pages/user-management-enterprise'));

const loading = () => <div className=""></div>;

type LoadComponentProps = {
    component: React.LazyExoticComponent<() => JSX.Element>,
};

const LoadComponent = ({ component: Component, ...rest }: LoadComponentProps) => (
    <Suspense fallback={loading()}>
        <Component {...rest} />
    </Suspense>
);

const OverviewTab = React.lazy(() => import('../pages/optimizations/OverviewTab'));
const CarriersTab = React.lazy(() => import('../pages/optimizations/CarriersTab'));
const PackagesTab = React.lazy(() => import('../pages/optimizations/PackagesTab'));
const InventoryTab = React.lazy(() => import('../pages/optimizations/InventoryTab'));
const NetworkTab = React.lazy(() => import('../pages/optimizations/NetworkTab'));
const ContractsTab = React.lazy(() => import('../pages/optimizations/ContractsTab'));
const ChargesReviewTab = React.lazy(() => import('../pages/optimizations/ChargesReviewTab'));
const ChargesSummaryTab = React.lazy(() => import('../pages/optimizations/ChargesSummaryTab'));

const OrderTab = React.lazy(() => import('../pages/analytics/OrderTab'));
const ShipmentsTab = React.lazy(() => import('../pages/analytics/ShipmentsTab'));
const ServiceLevelsTab = React.lazy(() => import('../pages/analytics/ServiceLevelsTab'));
const ZonesTab = React.lazy(() => import('../pages/analytics/ZonesTab'));
const TrackingStatusTab = React.lazy(() => import('../pages/analytics/DeliveryTrackingStatusTab'));
const TransitTimesTab = React.lazy(() => import('../pages/analytics/DeliveryTransitTimesTab'));
const DeliveryPerformanceTab = React.lazy(() => import('../pages/analytics/DeliveryPerformanceTab'));
const SavingsTab = React.lazy(() => import('../pages/analytics/SavingsTab'));
const BillingWeightTab = React.lazy(() => import('../pages/analytics/BillingWeightTab'));
const BillingSurchargesTab = React.lazy(() => import('../pages/analytics/BillingSurchargesTab'));
const WeatherImpact = React.lazy(() => import('../pages/warehouse-management/weather-impact'));

// Import the new tab components
const LineDetailsTab = React.lazy(() => import('../pages/shipment-comparison/LineDetailsTab'));
const TotalCostTab = React.lazy(() => import('../pages/shipment-comparison/TotalCostTab'));
const SurchargesTab = React.lazy(() => import('../pages/shipment-comparison/SurchargesTab'));
const ShipmentsCountTab = React.lazy(() => import('../pages/shipment-comparison/ShipmentsCountTab'));

const AllRoutes = () => {
    const { layout } = useSelector((state) => ({
        layout: state.Layout,
    }));

    const getLayout = () => {
        let layoutCls = VerticalLayout;

        switch (layout.layoutType) {
            case layoutConstants.LAYOUT_HORIZONTAL:
                layoutCls = HorizontalLayout;
                break;
            case layoutConstants.LAYOUT_DETACHED:
                layoutCls = DetachedLayout;
                break;
            case layoutConstants.LAYOUT_FULL:
                layoutCls = FullLayout;
                break;
            default:
                layoutCls = VerticalLayout;
                break;
        }
        return layoutCls;
    };
    let Layout = getLayout();

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'account',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'register', element: <LoadComponent component={Register} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                        { path: 'login2', element: <LoadComponent component={Login2} /> },
                        { path: 'register2', element: <LoadComponent component={Register2} /> },
                        { path: 'confirm2', element: <LoadComponent component={Confirm2} /> },
                        { path: 'forget-password2', element: <LoadComponent component={ForgetPassword2} /> },
                        { path: 'lock-screen2', element: <LoadComponent component={LockScreen2} /> },
                        { path: 'logout2', element: <LoadComponent component={Logout2} /> },
                    ],
                },
                { path: 'reset-password', element: <LoadComponent component={ResetPassword} /> },
                {
                    path: 'error-404',
                    element: <LoadComponent component={ErrorPageNotFound} />,
                },
                {
                    path: 'error-500',
                    element: <LoadComponent component={ServerError} />,
                },
                {
                    path: 'maintenance',
                    element: <LoadComponent component={Maintenance} />,
                },
            ],
        },
        {
            // auth protected routes
            path: '/',
            element: <PrivateRoute roles={'Admin'} component={Layout} />,
            children: [
                {
                    path: 'pages',
                    children: [
                        {
                            path: 'orders',
                            element: <LoadComponent component={Orders} />,
                        },
                        {
                            path: 'edit-order',
                            element: <LoadComponent component={EditOrder} />,
                        },
                        {
                            path: 'new-order',
                            element: <LoadComponent component={AddOrder} />,
                        },
                        {
                            path: 'view-order',
                            element: <LoadComponent component={ViewOrder} />,
                        },
                        {
                            path: 'shipments',
                            element: <LoadComponent component={Shipments} />,
                        },
                        {
                            path: 'agents',
                            element: <LoadComponent component={Agents} />,
                        },
                        {
                            path: 'pickups-schedule',
                            element: <LoadComponent component={PickupSchedule} />,
                        },
                        {
                            path: 'view-pickup',
                            element: <LoadComponent component={ViewPickup} />,
                        },
                        {
                            path: 'manifests',
                            element: <LoadComponent component={Manifests} />,
                        },
                        {
                            path: 'create-manifest',
                            element: <LoadComponent component={CreateManifest} />,
                        },
                        {
                            path: 'pickups',
                            element: <LoadComponent component={Pickup} />,
                        },
                        {
                            path: 'addresses',
                            element: <LoadComponent component={Addresses} />,
                        },
                        {
                            path: 'warehouses',
                            element: <LoadComponent component={Warehouses} />,
                        },
                        {
                            path: 'locations',
                            element: <LoadComponent component={Locations} />,
                        },
                        {
                            path: 'inventory',
                            element: <LoadComponent component={Inventory} />,
                        },
                        {
                            path: 'location',
                            element: <LoadComponent component={Location} />,
                        },
                        {
                            path: 'find-location',
                            element: <LoadComponent component={FindLocation} />,
                        },
                        {
                            path: 'view-location',
                            element: <LoadComponent component={ViewLocation} />,
                        },
                        {
                            path: 'manifest',
                            element: <LoadComponent component={Manifest} />,
                        },
                        {
                            path: 'profile',
                            element: <LoadComponent component={Profile2} />,
                        },
                        {
                            path: 'account-profile',
                            element: <LoadComponent component={ProfileAccount} />,
                        },
                        {
                            path: 'billing',
                            element: <LoadComponent component={Billing} />,
                        },
                        {
                            path: 'plan',
                            element: <LoadComponent component={Plan} />,
                        },
                        {
                            path: 'view-plans',
                            element: <LoadComponent component={ViewPlans} />,
                        },
                        {
                            path: 'invoice/:invoiceId/',
                            element: <LoadComponent component={ViewInvoice} />,
                        },
                        {
                            path: 'users',
                            element: <LoadComponent component={Users} />,
                        },
                        {
                            path: 'batches',
                            element: <LoadComponent component={Batches} />,
                        },
                        {
                            path: 'returns',
                            element: <LoadComponent component={Returns} />,
                        },
                        {
                            path: 'products',
                            element: <LoadComponent component={Products} />,
                        },
                        {
                            path: 'new-product',
                            element: <LoadComponent component={NewProduct} />,
                        },
                    ],
                },
                {
                    path: 'reports',
                    children: [
                        {
                            path: 'orders',
                            element: <LoadComponent component={OrderReport} />,
                        },
                        // {
                        //     path: 'products',
                        //     element: <LoadComponent component={ProductReport} />,
                        // },
                        {
                            path: 'shipments',
                            element: <LoadComponent component={ShipmentReport} />,
                        },
                        {
                            path: 'shipment-surcharges',
                            element: <LoadComponent component={ShipmentSurchargesReport} />,
                        },
                        {
                            path: 'non-shipment-surcharges',
                            element: <LoadComponent component={NonShipmentSurchargesReport} />,
                        },
                        {
                            path: 'adjustments',
                            element: <LoadComponent component={AdjustmentsStandardReport} />,
                        },
                        // {
                        //     path: 'charges',
                        //     element: <LoadComponent component={ChargesReport} />,
                        // },
                        {
                            path: 'tracking-records',
                            element: <LoadComponent component={TrackingRecordsReport} />,
                        },
                        {
                            path: 'delayed-facilities',
                            element: <LoadComponent component={DelayedFacilitiesReport} />,
                        },
                        {
                            path: 'aggregated-delayed-shipments',
                            element: <LoadComponent component={AggregatedDelayedShipmentsReport} />,
                        },
                        {
                            path: 'address-report',
                            element: <LoadComponent component={AddressReport} />,
                        },
                        // {
                        //     path: 'carriers',
                        //     element: <LoadComponent component={CarrierReport} />,
                        // },
                        // {
                        //     path: 'payments',
                        //     element: <LoadComponent component={PaymentReport} />,
                        // },
                        {
                            path: 'audit',
                            element: <LoadComponent component={AuditReport} />,
                        },
                        {
                            path: 'activity-log',
                            element: <LoadComponent component={ActivityLogReport} />,
                        },
                    ],
                },
                {
                    path: 'optimizations',
                    children: [
                        {
                            path: 'overview',
                            element: <LoadComponent component={OverviewTab} />,
                        },
                        {
                            path: 'carriers',
                            element: <LoadComponent component={CarriersTab} />,
                        },
                        {
                            path: 'packages',
                            element: <LoadComponent component={PackagesTab} />,
                        },
                        {
                            path: 'inventory',
                            element: <LoadComponent component={InventoryTab} />,
                        },
                        {
                            path: 'network',
                            element: <LoadComponent component={NetworkTab} />,
                        },
                        {
                            path: 'contracts',
                            element: <LoadComponent component={ContractsTab} />,
                        },
                        {
                            path: 'charges-review',
                            element: <LoadComponent component={ChargesReviewTab} />,
                        },
                        {
                            path: 'charges-summary',
                            element: <LoadComponent component={ChargesSummaryTab} />,
                        },
                    ],
                },
                {
                    path: 'analytics',
                    children: [
                        {
                            path: 'orders',
                            element: <LoadComponent component={OrderTab} />,
                        },
                        {
                            path: 'shipments',
                            element: <LoadComponent component={ShipmentsTab} />,
                        },
                        {
                            path: 'service-levels',
                            element: <LoadComponent component={ServiceLevelsTab} />,
                        },
                        {
                            path: 'zones',
                            element: <LoadComponent component={ZonesTab} />,
                        },
                        {
                            path: 'tracking-status',
                            element: <LoadComponent component={TrackingStatusTab} />,
                        },
                        {
                            path: 'transit-times',
                            element: <LoadComponent component={TransitTimesTab} />,
                        },
                        {
                            path: 'delivery-performance',
                            element: <LoadComponent component={DeliveryPerformanceTab} />,
                        },
                        {
                            path: 'savings',
                            element: <LoadComponent component={SavingsTab} />,
                        },
                        {
                            path: 'billed-weight',
                            element: <LoadComponent component={BillingWeightTab} />,
                        },
                        {
                            path: 'charges',
                            element: <LoadComponent component={BillingSurchargesTab} />,
                        },
                    ],
                },
                {
                    path: 'warehouse-management',
                    children: [
                        {
                            path: 'weather-impact',
                            element: <LoadComponent component={WeatherImpact} />,
                        },
                    ],
                },
                {
                    path: 'imports',
                    children: [
                        {
                            path: '',
                            element: <LoadComponent component={Imports} />,
                        },
                        {
                            path: 'manage',
                            element: <LoadComponent component={ImportManage} />,
                        },
                    ],
                },
                {
                    path: 'shipment-comparison',
                    children: [
                        {
                            path: 'line-details',
                            element: <LoadComponent component={LineDetailsTab} />,
                        },
                        {
                            path: 'total-cost',
                            element: <LoadComponent component={TotalCostTab} />,
                        },
                        {
                            path: 'surcharges',
                            element: <LoadComponent component={SurchargesTab} />,
                        },
                        {
                            path: 'shipments-count',
                            element: <LoadComponent component={ShipmentsCountTab} />,
                        },
                    ],
                },
                {
                    path: 'integrations',
                    children: [
                        {
                            path: 'ecommerce-technology',
                            element: <LoadComponent component={TechnologyPartners} />,
                        },
                        {
                            path: 'stores',
                            element: <LoadComponent component={StoreIntegrations} />,
                        },
                        // {
                        //     path: 'api',
                        //     element: <LoadComponent component={IntegrationApi} />,
                        // },
                        {
                            path: '/integrations/carrier-accounts',
                            element: <LoadComponent component={CarrierAccounts} />,
                        },
                        {
                            path: 'webhooks',
                            element: <LoadComponent component={Webhooks} />,
                        },
                        {
                            path: 'job-management',
                            element: <LoadComponent component={JobManagement} />,
                        },
                    ],
                },
                {
                    path: 'settings',
                    children: [
                        {
                            path: 'peripherals',
                            element: <LoadComponent component={Peripherals} />,
                        },
                        {
                            path: 'label-and-docs',
                            element: <LoadComponent component={LabelAndDocs} />,
                        },
                        {
                            path: 'automation',
                            element: <LoadComponent component={Automation} />,
                        },
                        {
                            path: 'add-automation',
                            element: <LoadComponent component={AddAutomation} />,
                        },
                        {
                            path: 'onboarding',
                            element: <LoadComponent component={Onboarding} />,
                        },
                        {
                            path: 'service-levels',
                            element: <LoadComponent component={ServiceLevels} />,
                        },
                        {
                            path: 'notifications',
                            element: <LoadComponent component={Notifications} />,
                        },
                        {
                            path: 'packages',
                            element: <LoadComponent component={Packages} />,
                        },
                        {
                            path: 'rates',
                            element: <LoadComponent component={RateSettings} />,
                        },
                        {
                            path: 'rate-sets',
                            element: <LoadComponent component={RateSets} />,
                        },
                    ],
                },
                {
                    path: 'home/enterprise-dashboard',
                    element: <LoadComponent component={EnterpriseDashboard} />,
                },
                {
                    path: 'analytics-enterprise',
                    children: [
                        {
                            path: 'shipments',
                            element: <LoadComponent component={ShipmentsAnalyticsEnterprise} />,
                        },
                        {
                            path: 'deliveries',
                            element: <LoadComponent component={DeliveriesAnalyticsEnterprise} />,
                        },
                        {
                            path: 'financials',
                            element: <LoadComponent component={FinancialsAnalyticsEnterprise} />,
                        },
                        {
                            path: 'custom',
                            element: <LoadComponent component={CustomAnalyticsEnterprise} />,
                        },
                        {
                            path: 'shipment-comparison',
                            element: <LoadComponent component={ShipmentComparisonEnterprise} />,
                        },
                    ],
                },
                {
                    path: 'reports-enterprise',
                    children: [
                        {
                            path: 'shipments',
                            element: <LoadComponent component={ShipmentsReports} />,
                        },
                        {
                            path: 'refunds',
                            element: <LoadComponent component={RefundsReports} />,
                        },
                        {
                            path: 'deliveries',
                            element: <LoadComponent component={DeliveriesReportsEnterprise} />,
                        },
                        {
                            path: 'charges',
                            element: <LoadComponent component={ChargesReport} />,
                        },
                        {
                            path: 'adjustments',
                            element: <LoadComponent component={AdjustmentsReport} />,
                        },
                        {
                            path: 'margins',
                            element: <LoadComponent component={MarginsReport} />,
                        },
                        {
                            path: 'carrier-accounts',
                            element: <LoadComponent component={CarrierAccountsReportEnterprise} />,
                        },
                        {
                            path: 'custom-accounting',
                            element: <LoadComponent component={CustomAccountingReport} />,
                        },
                        {
                            path: 'custom-customer',
                            element: <LoadComponent component={CustomCustomerReport} />,
                        },
                        {
                            path: 'custom-profitability',
                            element: <LoadComponent component={CustomProfitabilityReport} />,
                        },
                        {
                            path: 'custom-adjustments',
                            element: <LoadComponent component={CustomAdjustmentsReport} />,
                        },
                        {
                            path: 'custom-verified-margins',
                            element: <LoadComponent component={CustomVerifiedMarginsReport} />,
                        },
                    ],
                },
                {
                    path: 'account-management-enterprise',
                    element: <LoadComponent component={AccountManagementEnterprise} />,
                },
                {
                    path: 'user-management-enterprise',
                    element: <LoadComponent component={UserManagementEnterprise} />,
                },
            ],
        },
    ]);
};

export { AllRoutes };
